import { Card, CardBody, Col, Form, FormGroup, Input, Label, Row, Table } from 'reactstrap';
import React, { useState, useCallback } from 'react';
import * as yup from 'yup';

import { Button } from '../../components';
import { toast } from 'react-toastify';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { AccreditationActionTotalReportDto } from '../../common/Types';
import { reportService } from '../../services/ReportService';
import moment from 'moment';

// Form data interface
interface AnnualReportTotalsReportFormData {
    startDate: string;
    endDate: string;
    concentrations: boolean;
}

// Validation schema
const validationSchema = yup.object({
    startDate: yup.string().required('Start date is required'),
    endDate: yup
        .string()
        .required('End date is required')
        .test('date-range', 'End date must be after start date', function (value) {
            const { startDate } = this.parent;
            if (!startDate || !value) return true;
            return moment(value).isSameOrAfter(moment(startDate));
        }),
    concentrations: yup.boolean().required().default(false),
});

const AnnualReportTotalsReport = () => {
    const [actionTotals, setActionTotals] = useState<AccreditationActionTotalReportDto>();

    const {
        control,
        handleSubmit,
        reset,
        formState: { errors, isDirty },
        watch,
    } = useForm<AnnualReportTotalsReportFormData>({
        resolver: yupResolver(validationSchema),
        mode: 'onChange',
        defaultValues: {
            startDate: '',
            endDate: '',
            concentrations: false,
        },
    });

    const watchedValues = watch();

    const clearSearchForm = useCallback(() => {
        setActionTotals(undefined);
        reset({
            startDate: '',
            endDate: '',
            concentrations: false,
        });
    }, [reset]);

    const onSubmit = useCallback((data: AnnualReportTotalsReportFormData) => {
        const toastId = toast.info('Searching...', {
            autoClose: false,
        });
        reportService
            .getAccreditationActionTotalsReport(
                moment(data.startDate).format(),
                moment(data.endDate).format(),
                data.concentrations,
            )
            .then((response) => {
                setActionTotals(response);
                toast.update(toastId, {
                    render: 'Report generated successfully.',
                    type: 'success',
                    autoClose: 500,
                    closeOnClick: true,
                    draggable: true,
                });
            })
            .catch(() => {
                toast.update(toastId, {
                    render: 'Failed to generate report',
                    type: 'error',
                    autoClose: 5000,
                    closeOnClick: true,
                    draggable: true,
                });
            });
    }, []);

    const exportReport = useCallback(() => {
        if (!watchedValues.startDate || !watchedValues.endDate) {
            toast.error('Please select start and end dates before exporting');
            return;
        }

        const toastId = toast.info('Generating report, this may take a minute...', {
            autoClose: false,
        });
        reportService
            .exportAccreditationActionTotalsReport(
                moment(watchedValues.startDate).format(),
                moment(watchedValues.endDate).format(),
                watchedValues.concentrations,
            )
            .then((report) => {
                if (report) {
                    const url = window.URL.createObjectURL(report);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = `AccreditationActionTotalReport.xlsx`;
                    a.click();
                    toast.update(toastId, {
                        render: 'Report generated successfully.',
                        type: 'success',
                        autoClose: 2500,
                        closeOnClick: true,
                        draggable: true,
                    });
                }
            })
            .catch(() => {
                toast.update(toastId, {
                    render: 'Unable to generate report',
                    type: 'error',
                    autoClose: 5000,
                    closeOnClick: true,
                    draggable: true,
                });
            });
    }, [watchedValues.startDate, watchedValues.endDate, watchedValues.concentrations]);

    return (
        <>
            <Card>
                <CardBody>
                    <Row>
                        <Col>
                            <div className="mb-3">
                                <h3>Annual Report Totals</h3>
                            </div>
                            <Form noValidate onSubmit={handleSubmit(onSubmit)}>
                                <div className="d-flex align-items-center flex-wrap" style={{ gap: '1rem' }}>
                                <div className="d-flex align-items-center" style={{ gap: '0.5rem' }}>
                                    <Controller
                                        name="concentrations"
                                        control={control}
                                        render={({ field }) => (
                                            <div className={'d-flex justify-content-between p-2'}>
                                                <div className={'custom-control custom-radio pr-2'}>
                                                    <input
                                                        type={'radio'}
                                                        id={`radioAll`}
                                                        className={'custom-control-input'}
                                                        checked={field.value === true}
                                                        onChange={() => field.onChange(true)}
                                                        value="1"
                                                    />
                                                    <label
                                                        className={'custom-control-label'}
                                                        htmlFor={`radioAll`}
                                                    >{`With Concentrations`}</label>
                                                </div>
                                                <div className={'custom-control custom-radio pr-2'}>
                                                    <input
                                                        type={'radio'}
                                                        id={`radioUnmerged`}
                                                        className={'custom-control-input'}
                                                        checked={field.value === false}
                                                        onChange={() => field.onChange(false)}
                                                        value="0"
                                                    />
                                                    <label
                                                        className={'custom-control-label'}
                                                        htmlFor={`radioUnmerged`}
                                                    >{`Without Concentrations`}</label>
                                                </div>
                                            </div>
                                        )}
                                    />
                                    {errors.concentrations && (
                                        <div className="text-danger small mt-1">{errors.concentrations.message}</div>
                                    )}
                                </div>
                                <div className="d-flex align-items-center" style={{ gap: '0.5rem' }}>
                                    <Label for="startDate" className="mb-0" style={{ whiteSpace: 'nowrap' }}>
                                        Start Date:
                                    </Label>
                                    <Controller
                                        name="startDate"
                                        control={control}
                                        render={({ field }) => (
                                            <Input
                                                type="date"
                                                {...field}
                                                value={field.value || ''}
                                                onChange={(e) => field.onChange(e.target.value)}
                                            />
                                        )}
                                    />
                                    {errors.startDate && (
                                        <div className="text-danger small mt-1">{errors.startDate.message}</div>
                                    )}
                                </div>
                                <div className="d-flex align-items-center" style={{ gap: '0.5rem' }}>
                                    <Label for="endDate" className="mb-0" style={{ whiteSpace: 'nowrap' }}>
                                        End Date:
                                    </Label>
                                    <Controller
                                        name="endDate"
                                        control={control}
                                        render={({ field }) => (
                                            <Input
                                                type="date"
                                                {...field}
                                                value={field.value || ''}
                                                onChange={(e) => field.onChange(e.target.value)}
                                            />
                                        )}
                                    />
                                    {errors.endDate && (
                                        <div className="text-danger small mt-1">{errors.endDate.message}</div>
                                    )}
                                </div>
                                </div>

                                {/* Button row within the form */}
                                <Row className="mt-3">
                                    <Col className="d-flex justify-content-between align-items-center">
                                        <div className="d-flex align-items-center" style={{ gap: '0.5rem' }}>
                                            <Button
                                                type="submit"
                                                color="primary"
                                            >
                                                Search
                                            </Button>
                                            <Button
                                                type="button"
                                                color="link"
                                                onClick={clearSearchForm}
                                            >
                                                Clear
                                            </Button>
                                        </div>
                                        <Button
                                            type="button"
                                            color="info"
                                            onClick={exportReport}
                                            disabled={!watchedValues.startDate || !watchedValues.endDate}
                                        >
                                            Export Report
                                        </Button>
                                    </Col>
                                </Row>
                            </Form>
                        </Col>
                    </Row>
                    <div className="mt-3">
                        <h4>Accredited Programs</h4>
                    </div>
                </CardBody>
                <Table>
                    <thead>
                        <tr>
                            <th>Professions</th>
                            <th>Initial</th>
                            <th>Continuing</th>
                            <th>Probation</th>
                            <th>Withdrawn and/or Withheld</th>
                            <th>Initial Expire</th>
                            <th>Total</th>
                        </tr>
                    </thead>
                    <tbody>
                        {actionTotals?.professionAccreditationActions ? (
                            actionTotals.professionAccreditationActions.map((record) => (
                                <tr key={record.professionId}>
                                    <td>{record.professionTitle}</td>
                                    <td>{record.initialCount}</td>
                                    <td>{record.continuingCount}</td>
                                    <td>{record.probationCount}</td>
                                    <td>{record.withdrawnCount}</td>
                                    <td>{record.expireCount}</td>
                                    <td>{record.totalCount}</td>
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td colSpan={7}>No Results</td>
                            </tr>
                        )}
                    </tbody>
                    <tfoot>
                        <tr>
                            <th>Total</th>
                            <th>{actionTotals?.initialTotal}</th>
                            <th>{actionTotals?.continuingTotal}</th>
                            <th>{actionTotals?.probationTotal}</th>
                            <th>{actionTotals?.withdrawnTotal}</th>
                            <th>{actionTotals?.expireTotal}</th>
                            <th>{actionTotals?.totalTotal}</th>
                        </tr>
                    </tfoot>
                </Table>
            </Card>
        </>
    );
};

export default AnnualReportTotalsReport;
